/**
 * S23 — context-menu action list for a `dashboard` tree node: Open, Present
 * (S13), Settings…, Duplicate, Delete. Later slices append their own items
 * (S14 "Export definition…") — keep this list-returning function the single
 * extension point.
 *
 * The "Settings…" item (actionId stays `'rename'` — see
 * `dashboardExplorerActions.ts` and `DatabaseExplorer.tsx`'s
 * `actionId === 'rename'` branch) opens the shared multi-section
 * `DashboardSettingsDialog` (rename + parameters + playlist), matching the
 * label the workspace tab's own kebab already uses for the identical dialog
 * (`DashboardToolbar.tsx`'s `dashboard-settings-item`) — a UX review flagged
 * "Rename" as a mismatch once the dialog covers more than the name field.
 */
import type { ContextMenuAction } from '../../../explorer/models/TreeModel';
import { FREE_DASHBOARD_LIMIT } from '../types/dashboardTypes';

export type DashboardContextMenuOptions = {
  /** True when duplicating would hit the free-tier cap (non-Pro only). */
  duplicateBlocked: boolean;
};

export function generateDashboardContextMenuActions(
  options: DashboardContextMenuOptions,
): ContextMenuAction[] {
  const { duplicateBlocked } = options;

  return [
    { id: 'open', label: 'Open', icon: 'play' },
    { id: 'present', label: 'Present', icon: 'maximize' },
    { id: 'rename', label: 'Settings…', icon: 'edit' },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: 'copy',
      disabled: duplicateBlocked,
      disabledReason: duplicateBlocked
        ? `Free plan is limited to ${FREE_DASHBOARD_LIMIT} dashboards — upgrade to Pro for unlimited.`
        : undefined,
    },
    { id: 'export-definition', label: 'Export definition…', icon: 'download' },
    { id: 'separator', label: '', isSeparator: true },
    { id: 'delete', label: 'Delete', icon: 'trash' },
  ];
}
